import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import L from 'leaflet';
import "leaflet/dist/leaflet.css";
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { getMapData } from '../../../Services/authService';
import './MapComponent.css';
import { useTranslation } from "react-i18next";
import { useThemeContext } from '../../../Context/ThemeContext';

import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
// MapTiler styles — same key, different variant per theme
const TILE_URLS = {
  light: 'https://api.maptiler.com/maps/bright-v2/256/{z}/{x}/{y}.png?key=shSU7ahs2GW4TBLvmsRK',
  dark: 'https://api.maptiler.com/maps/streets-v2-dark/256/{z}/{x}/{y}.png?key=shSU7ahs2GW4TBLvmsRK'
};

const isValidCoordinate = (lat, lng) => {
  const validLat = !isNaN(Number(lat)) && Number(lat) >= -90 && Number(lat) <= 90;
  const validLng = !isNaN(Number(lng)) && Number(lng) >= -180 && Number(lng) <= 180;
  return validLat && validLng;
};

const MapComponent = () => {
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';

  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  const tileLayerRef = useRef(null);
  const markersLayer = useRef(null);
  const markers = useRef(new Map());
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const clusterEventHandlers = useRef(null);

  // Initialize map only after component is mounted
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getMapData();
        if (response?.data) {
          const transformedLocations = response.data.map(location => ({
            id: location.id,
            machineNumber: location.machineNumber,
            name: location.location,
            lat: location.latitude,
            lng: location.longitude,
          }));

          const validLocations = transformedLocations.filter(
            location => isValidCoordinate(location.lat, location.lng)
          );

          if (validLocations.length === 0) {
            throw new Error(t('dashboard.noLocations'));
          }
          setMapData(validLocations);
          // console.log('Map data fetched and processed:', validLocations);
          setFilteredLocations(validLocations);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (err) {
        console.error(t('dashboard.errorFetchingMapData'), err);
        setError(err?.message || t("dashboard.errorFetchingMapData"));
      } finally {
        setLoading(false);
      }
    };

    fetchMapData();
  }, [t]);

  // Cleanup helper
  const cleanup = useCallback(() => {
    if (markersLayer.current && clusterEventHandlers.current) {

      markersLayer.current.off('animationend', clusterEventHandlers.current.syncPopupsToClusterState);
      clusterEventHandlers.current = null;
    }
    if (markersLayer.current) {
      markersLayer.current.clearLayers();
    }
    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }
    tileLayerRef.current = null;
    markers.current.clear();
  }, []);


  const containerBackground = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const textColor = mode === 'dark' ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;
  const bodyColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
  const inputBg = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const borderColor = mode === 'dark' ? DARKCOLORS.GreyScale20 : LIGHTCOLORS.GreyScale20;
  const chipBg = mode === 'dark' ? DARKCOLORS.BackgroundMenu : LIGHTCOLORS.BackgroundMenu;
  const activechipBg = mode === 'dark' ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale20;


  const createPopupContent = useCallback((location) => {
    const bgColor = containerBackground;
    const iconFill = bodyColor;
    const iconCircle = bodyColor;
    const titleColor = textColor;
    const subtitleColor = textColor;

    return `
      <div style="
        font-family: Arial, sans-serif;
        background-color: ${bgColor};
        line-height: 1.2;
        font-size: 12px;
        display: inline-flex;
        padding: 15px 20px 15px 15px;
        align-items: center;
        gap: 8px;
        border-radius: 20px;
      ">
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34" fill="none">
            <circle opacity="0.1" cx="17" cy="17" r="17" fill="${iconCircle}"/>
            <path d="M22.0263 7.5H10.9737C9.88421 7.5 9 8.32963 9 9.35185V24.1667C9 24.7704 9.31185 25.3 9.78947 25.6407V26.3889C9.78947 27 10.3224 27.5 10.9737 27.5H22.0263C22.6776 27.5 23.2105 27 23.2105 26.3889V25.6407C23.6882 25.3037 24 24.7704 24 24.1667V9.35185C24 8.32963 23.1158 7.5 22.0263 7.5ZM22.4211 26.0185V26.3889C22.4211 26.5926 22.2434 26.7592 22.0263 26.7592H10.9737C10.7566 26.7592 10.5789 26.5926 10.5789 26.3889V26.0185H22.4211ZM23.2105 24.1667C23.2105 24.7778 22.6776 25.2778 22.0263 25.2778H10.9737C10.3224 25.2778 9.78947 24.7778 9.78947 24.1667V9.35185C9.78947 8.74075 10.3224 8.24074 10.9737 8.24074H22.0263C22.6776 8.24074 23.2105 8.74075 23.2105 9.35185V24.1667Z" fill="${iconFill}"/>
            <path d="M18.4736 8.98136H11.7632C11.1118 8.98136 10.5789 9.48137 10.5789 10.0925V19.7221C10.5789 20.3332 11.1118 20.8332 11.7632 20.8332H18.4736C19.1249 20.8332 19.6578 20.3332 19.6578 19.7221V10.0925C19.6578 9.48137 19.1249 8.98136 18.4736 8.98136ZM18.8683 19.722C18.8683 19.9257 18.6907 20.0924 18.4736 20.0924H11.7632C11.5461 20.0924 11.3684 19.9257 11.3684 19.722V10.0924C11.3684 9.88865 11.5461 9.72199 11.7632 9.72199H18.4736C18.6907 9.72199 18.8683 9.88865 18.8683 10.0924V19.722Z" fill="${iconFill}"/>
            <path d="M22.0263 22.6852H20.8421C20.625 22.6852 20.4474 22.8518 20.4474 23.0555C20.4474 23.2593 20.625 23.4259 20.8421 23.4259H22.0263C22.2434 23.4259 22.4211 23.2593 22.4211 23.0555C22.4211 22.8518 22.2434 22.6852 22.0263 22.6852Z" fill="${iconFill}"/>
            <path d="M18.4736 21.5742H11.7632C11.1118 21.5742 10.5789 22.0742 10.5789 22.6853V23.426C10.5789 24.0371 11.1118 24.5371 11.7632 24.5371H18.4736C19.1249 24.5371 19.6578 24.0371 19.6578 23.426V22.6853C19.6578 22.0742 19.1249 21.5742 18.4736 21.5742ZM18.8683 23.426C18.8683 23.6297 18.6907 23.7964 18.4736 23.7964H11.7632C11.5461 23.7964 11.3684 23.6297 11.3684 23.426V22.6853C11.3684 22.4816 11.5461 22.3149 11.7632 22.3149H18.4736C18.6907 22.3149 18.8683 22.4816 18.8683 22.6853V23.426Z" fill="${iconFill}"/>
            <path d="M20.8422 19.3517C21.0593 19.3517 21.237 19.1851 21.237 18.9814V18.2406C21.237 18.0369 21.0593 17.8702 20.8422 17.8702C20.6251 17.8702 20.4475 18.0369 20.4475 18.2406V18.9814C20.4475 19.1851 20.6251 19.3517 20.8422 19.3517Z" fill="${iconFill}"/>
            <path d="M22.0263 17.8702C21.8092 17.8702 21.6316 18.0369 21.6316 18.2406V18.9814C21.6316 19.1851 21.8092 19.3517 22.0263 19.3517C22.2434 19.3517 22.4211 19.1851 22.4211 18.9814V18.2406C22.4211 18.0369 22.2434 17.8702 22.0263 17.8702Z" fill="${iconFill}"/>
            <path d="M18.0789 14.537H12.1578C11.9407 14.537 11.763 14.7037 11.763 14.9074C11.763 15.1111 11.9407 15.2778 12.1578 15.2778H18.0789C18.2961 15.2778 18.4737 15.1111 18.4737 14.9074C18.4737 14.7037 18.2961 14.537 18.0789 14.537Z" fill="${iconFill}"/>
            <path d="M18.0789 17.1297H12.1578C11.9407 17.1297 11.763 17.2964 11.763 17.5001C11.763 17.7038 11.9407 17.8705 12.1578 17.8705H18.0789C18.2961 17.8705 18.4737 17.7038 18.4737 17.5001C18.4737 17.2964 18.2961 17.1297 18.0789 17.1297Z" fill="${iconFill}"/>
            <path d="M18.0789 11.9443H12.1578C11.9407 11.9443 11.763 12.111 11.763 12.3147C11.763 12.5184 11.9407 12.6851 12.1578 12.6851H18.0789C18.2961 12.6851 18.4737 12.5184 18.4737 12.3147C18.4737 12.111 18.2961 11.9443 18.0789 11.9443Z" fill="${iconFill}"/>
            <path d="M20.1158 11.0556C20.1158 10.7937 20.342 10.5815 20.6211 10.5815H22.1368C22.4159 10.5815 22.6421 10.7937 22.6421 11.0556V16.5074C22.6421 16.7692 22.4159 16.9815 22.1368 16.9815H20.6211C20.342 16.9815 20.1158 16.7692 20.1158 16.5074V11.0556Z" fill="${iconFill}"/>
          </svg>
        </div>

        <div>
  <h3
    title="${location.name}"
    style="
      font-size: 15px;
      font-style: normal;
      font-weight: 500;
      line-height: 120%;
      letter-spacing: 0.3px;
      margin: 0;
      color: ${titleColor};

      // max-width: 100px;
      // width: 100px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      cursor: pointer;
    "
  >
    ${location.name}
  </h3>

  <p
    style="
      margin: 0;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 100%;
      letter-spacing: 0.26px;
      color: ${subtitleColor};
    "
  >
    ${location.machineNumber}
  </p>
</div>
      </div>
    `;
  }, [isDark]);

  // Update markers visibility with animation
  const updateMarkers = useCallback((locations) => {
    if (!mapInstance.current || !markersLayer.current) return;

    const bounds = L.latLngBounds();
    let hasValidMarkers = false;

    markers.current.forEach((marker, id) => {
      const location = locations.find(loc => loc.id === id);
      if (location) {
        marker.setOpacity(1);
        bounds.extend([location.lat, location.lng]);
        hasValidMarkers = true;
      } else {
        marker.setOpacity(0);
      }
    });

    if (hasValidMarkers && bounds.isValid()) {
      mapInstance.current.flyToBounds(bounds, {
        padding: [50, 50],
        maxZoom: 4,
        duration: 0.8
      });
    }
  }, []);

  const debouncedSearch = useMemo(() => {
    return (term, data) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        if (!data?.length) return;
        const filtered = term
          ? data.filter(loc => loc.name?.toLowerCase().includes(term.toLowerCase()))
          : data;
        setFilteredLocations(filtered);
        updateMarkers(filtered);
      }, 300);
    };
  }, [updateMarkers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    debouncedSearch(searchTerm, mapData);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, mapData, debouncedSearch]);


  // Theme-aware marker colors
  const markerFillColor = mode === 'dark' ? DARKCOLORS.Primary : LIGHTCOLORS.Primary;
  const markerInnerColor = mode === 'dark' ? '#78F080' : 'Black';


  useEffect(() => {
    if (!isMounted || !mapContainer.current || !filteredLocations?.length) return;

    cleanup();

    mapInstance.current = L.map(mapContainer.current, {
      zoomControl: false,
      closePopupOnClick: false,
      maxBounds: [[-90, -180], [90, 180]],
      maxBoundsViscosity: 1.0,
      minZoom: 2,
      maxZoom: 18
    });

    // Fit map to marker bounds
    const bounds = L.latLngBounds(
      filteredLocations.map(location => [location.lat, location.lng])
    );
    if (bounds.isValid()) {
      mapInstance.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }

    // Theme-aware tile layer — stored in ref so we can swap URL without re-init
    tileLayerRef.current = L.tileLayer(isDark ? TILE_URLS.dark : TILE_URLS.light, {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(mapInstance.current);

    // Only cluster when there are 10 or more locations
    const shouldCluster = filteredLocations.length >= 5;
    if (shouldCluster) {
      markersLayer.current = L.markerClusterGroup({
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: false,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: 16,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          return L.divIcon({
            html: `<div class="custom-cluster-icon ${isDark ? 'custom-cluster-icon--dark' : 'custom-cluster-icon--light'}"><span>${count}</span></div>`,
            className: 'marker-cluster-custom',
            iconSize: L.point(40, 40),
          });
        }
      }).addTo(mapInstance.current);

      const syncPopupsToClusterState = () => {
        if (!mapInstance.current) return;
        markers.current.forEach((marker) => {
          if (!marker.getPopup()) return;
          if (marker._map) {
            marker.openPopup();
          } else {
            marker.closePopup();
          }
        });
      };

      markersLayer.current.on('animationend', syncPopupsToClusterState);

      clusterEventHandlers.current = { syncPopupsToClusterState };
    } else {
      markersLayer.current = L.layerGroup().addTo(mapInstance.current);
    }

    const createCustomIcon = () => {
      const svgString = `
<svg xmlns="http://www.w3.org/2000/svg" width="27" height="27" viewBox="0 0 32 32">
  <circle 
    cx="16" 
    cy="16" 
    r="16" 
    fill="${markerFillColor}" 
    fill-opacity="0.2"
  />
  
  <circle 
    cx="16" 
    cy="16" 
    r="8" 
    fill="${markerInnerColor}"
  />
</svg>
`;

      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      return L.icon({
        iconUrl: svgUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [42, -32]
      });
    };

    const customIcon = createCustomIcon();

    // Add markers + popups
    filteredLocations.forEach(location => {
      if (!isValidCoordinate(location.lat, location.lng)) return;

      const marker = L.marker([location.lat, location.lng], { icon: customIcon });
      const popup = L.popup({
        closeButton: false,
        closeOnClick: false,
        autoClose: false,
        className: `permanent-popup ${isDark ? 'permanent-popup--dark' : 'permanent-popup--light'}`,
        offset: [-25, 30]
      }).setContent(createPopupContent(location));

      marker.bindPopup(popup);
      marker.addTo(markersLayer.current);
      markers.current.set(location.id, marker);
    });

    requestAnimationFrame(() => {
      markers.current.forEach((marker) => {
        if (marker.getPopup() && marker._map) {
          marker.openPopup();
        }
      });

      
      markers.current.forEach(marker => {
        const popupEl = marker.getPopup()?.getElement();
        if (!popupEl) return;
        L.DomEvent.on(popupEl, 'wheel', (e) => {
          L.DomEvent.stopPropagation(e);
          if (mapInstance.current) {
            mapInstance.current.scrollWheelZoom._onWheelScroll(e);
          }
        });
      });
    });

    return cleanup;
  }, [isMounted, filteredLocations, isDark, cleanup, createPopupContent]);

  if (error) {
  return (
    <div className={`map-error ${isDark ? 'map-error--dark' : 'map-error--light'}`}>
      <p>{error}</p>
    </div>
  );
}

  return (
    <div className={`map-wrapper ${isDark ? 'map-wrapper--dark' : 'map-wrapper--light'}`}>
      <div className="map-search-dropdown" ref={dropdownRef}>
        <div className="map-search-input-wrapper">
          <input
            type="text"
            placeholder={t("dashboard.Searchlocations")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            id='input-map'
            className="map-search-input"
          />
          {searchTerm && (
            <button
              type="button"
              className="map-search-clear-btn"
              onClick={() => {
                setSearchTerm('');
                setShowDropdown(true);
              }}
              aria-label="Clear search"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.8536 2.85355C13.0488 2.65829 13.0488 2.34171 12.8536 2.14645C12.6583 1.95118 12.3417 1.95118 12.1464 2.14645L8 6.29289L3.85355 2.14645C3.65829 1.95118 3.34171 1.95118 3.14645 2.14645C2.95118 2.34171 2.95118 2.65829 3.14645 2.85355L7.29289 7L3.14645 11.1464C2.95118 11.3417 2.95118 11.6583 3.14645 11.8536C3.34171 12.0488 3.65829 12.0488 3.85355 11.8536L8 7.70711L12.1464 11.8536C12.3417 12.0488 12.6583 12.0488 12.8536 11.8536C13.0488 11.6583 13.0488 11.3417 12.8536 11.1464L8.70711 7L12.8536 2.85355Z" fill="currentColor"/>
              </svg>
            </button>
          )}
        </div>
        {showDropdown && filteredLocations.length > 0 && (
          <ul className={`map-dropdown-list ${isDark ? 'map-dropdown-list--dark' : 'map-dropdown-list--light'}`}>
            {filteredLocations.map((location) => (
              <li
                key={location.id}
                className="map-dropdown-item"
                onClick={() => {
                  setSearchTerm(location.name);
                  setShowDropdown(false);
                  if (mapInstance.current && isValidCoordinate(location.lat, location.lng)) {
                    mapInstance.current.flyTo([location.lat, location.lng], 14, { duration: 1.5 });
                    // Open popup for the selected marker
                    const marker = markers.current.get(location.id);
                    if (marker) {
                      marker.openPopup();
                    }
                  }
                }}
              >
                <span className="map-dropdown-item-name" title={location.name}>{location.name}</span>
                <span className="map-dropdown-item-id">{location.machineNumber}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {filteredLocations.length === 0 && !loading ? (
        <div className="map-empty-state">
          {t('dashboard.noLocations')}
        </div>
      ) : null}
      <div ref={mapContainer} className="map-canvas" />
    </div>
  );
};

export default MapComponent;
